<template>
	<el-form ref="formRef" :model="form" :rules="rules" :label-width="labelWidth" class="password-form">
		<el-form-item prop="password" :label="oldPasswordLabel">
			<el-input v-model="form.password" type="password" :placeholder="oldPasswordPlaceholder" show-password clearable @keyup.enter="handleEnterKey" />
		</el-form-item>

		<el-form-item prop="newPassword" :label="newPasswordLabel">
			<el-input
				v-model="form.newPassword"
				type="password"
				:placeholder="newPasswordPlaceholder"
				show-password
				clearable
				@keyup.enter="handleEnterKey"
			/>
		</el-form-item>

		<el-form-item prop="confirmPassword" :label="confirmPasswordLabel">
			<el-input
				v-model="form.confirmPassword"
				type="password"
				:placeholder="confirmPasswordPlaceholder"
				show-password
				clearable
				@keyup.enter="handleEnterKey"
			/>
		</el-form-item>

		<slot name="footer" :validate="validate" :reset-form="resetForm" :handle-enter-key="handleEnterKey"></slot>
	</el-form>
</template>

<script setup lang="ts">
import { ref, reactive, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { validatePassword } from '@/utils/validate'

interface Props {
	modelValue?: Record<string, any>
	labelWidth?: string
	useI18nLabels?: boolean
}

interface Emits {
	(e: 'update:modelValue', value: Record<string, any>): void
	(e: 'enterKeyPressed'): void
}

const props = withDefaults(defineProps<Props>(), {
	labelWidth: 'auto',
	useI18nLabels: false
})

const emit = defineEmits<Emits>()
const { t } = useI18n()
const formRef = ref()

// 表单数据 - 直接使用默认的属性名初始化
const form = reactive<Record<string, string>>({
	password: '',
	newPassword: '',
	confirmPassword: ''
})

// 标签文本
const oldPasswordLabel = computed(() => {
	return props.useI18nLabels ? t('profileModule.profile.oldPassword') : '原密码'
})

const newPasswordLabel = computed(() => {
	return props.useI18nLabels ? t('profileModule.profile.newPassword') : '新密码'
})

const confirmPasswordLabel = computed(() => {
	return props.useI18nLabels ? t('profileModule.profile.confirmPassword') : '确认密码'
})

// 占位符文本
const oldPasswordPlaceholder = computed(() => {
	return `${oldPasswordLabel.value}`
})

const newPasswordPlaceholder = computed(() => {
	return `${newPasswordLabel.value}`
})

const confirmPasswordPlaceholder = computed(() => {
	return `${newPasswordLabel.value}`
})

// 验证规则
const validateOldPassword = (rule: any, value: string, callback: any) => {
	if (!value) {
		callback(new Error(t('profile.passwordRequired')))
	} else {
		callback()
	}
}

const validateNewPassword = (rule: any, value: string, callback: any) => {
	if (!value) {
		callback(new Error(t('profile.passwordRequired')))
	} else {
		// 使用统一的密码验证函数
		validatePassword(rule, value, callback)
	}
}

const validateConfirmPassword = (rule: any, value: string, callback: any) => {
	if (!value) {
		callback(new Error(t('profile.passwordRequired')))
	} else if (value !== form.newPassword) {
		callback(new Error(t('components.passwordForm.passwordNotMatch')))
	} else {
		callback()
	}
}

const rules = computed(() => ({
	password: [{ required: true, validator: validateOldPassword, trigger: 'blur' }],
	newPassword: [{ required: true, validator: validateNewPassword, trigger: 'blur' }],
	confirmPassword: [{ required: true, validator: validateConfirmPassword, trigger: 'blur' }]
}))

// 监听表单数据变化
watch(
	form,
	newVal => {
		emit('update:modelValue', { ...newVal })
	},
	{ deep: true }
)

// 监听外部数据变化
watch(
	() => props.modelValue,
	newVal => {
		if (newVal) {
			form.password = newVal.password || ''
			form.newPassword = newVal.newPassword || ''
			form.confirmPassword = newVal.confirmPassword || ''
		}
	},
	{ immediate: true, deep: true }
)

// 表单验证
const validate = async (): Promise<boolean> => {
	if (!formRef.value) {
		return false
	}

	try {
		await formRef.value.validate()
		return true
	} catch {
		return false
	}
}

// 重置表单
const resetForm = () => {
	form.password = ''
	form.newPassword = ''
	form.confirmPassword = ''
	formRef.value?.clearValidate()
}

// 处理Enter键按下事件
const handleEnterKey = async () => {
	const isValid = await validate()
	if (isValid) {
		emit('enterKeyPressed')
	}
}

// 暴露方法
defineExpose({
	validate,
	resetForm,
	handleEnterKey,
	form
})
</script>

<style lang="scss" scoped>
.password-form {
	// 可以根据需要添加样式
}
</style>
